import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://digilocker.srpfpunegroup1.in/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased from 10s to 30s for document loading
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= exp;
  } catch (e) {
    return true; // If can't decode, consider expired
  }
};

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Skip refresh logic for refresh endpoint itself or if explicitly skipped
    if (originalRequest.url?.includes('/auth/refresh') || originalRequest.skipAuthRefresh) {
      return Promise.reject(error);
    }
    
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = authToken.getRefresh();
      
      // If no refresh token or refresh token expired, redirect to login
      if (!refreshToken || isTokenExpired(refreshToken)) {
        isRefreshing = false;
        processQueue(new Error('Refresh token expired'), null);
        authToken.clear();
        // Redirect to login by reloading the page (will trigger App.js to show login)
        window.location.href = '/';
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token (use direct API call to avoid circular dependency)
        // Create a new axios instance without interceptors for refresh call
        const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken })
        });
        
        if (!refreshResponse.ok) {
          throw new Error('Token refresh failed');
        }
        
        const response = await refreshResponse.json();
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        
        // Update tokens
        authToken.set(accessToken, newRefreshToken);
        
        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        
        isRefreshing = false;
        processQueue(null, accessToken);
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        isRefreshing = false;
        processQueue(refreshError, null);
        authToken.clear();
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Employee API
export const employeeAPI = {
  getAll: (page = 1, pageSize = 10, groupId = null, consent = null) => {
    const params = new URLSearchParams({ page, pageSize });
    if (groupId) params.append('group_id', groupId);
    if (consent !== null && consent !== '') {
      params.append('consent', consent === true || consent === 'true' ? 'true' : 'false');
    }
    return api.get(`/employees?${params}`);
  },
  search: (query, page = 1, pageSize = 10) => api.get(`/employees/search?q=${encodeURIComponent(query)}&page=${page}&pageSize=${pageSize}`),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data, photoFile = null, signatureFile = null) => {
    // If files are provided, send as FormData
    if (photoFile || signatureFile) {
      const form = new FormData();
      // Append all employee data fields
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          form.append(key, data[key]);
        }
      });
      // Append files if provided
      if (photoFile) {
        form.append('photo', photoFile);
      }
      if (signatureFile) {
        form.append('signature', signatureFile);
      }
      return api.post('/employees', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    // Otherwise send as JSON
    return api.post('/employees', data);
  },
  update: (id, data, photoFile = null, signatureFile = null) => {
    // If files are provided, send as FormData
    if (photoFile || signatureFile) {
      const form = new FormData();
      // Append all employee data fields
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          form.append(key, data[key]);
        }
      });
      // Append files if provided
      if (photoFile) {
        form.append('photo', photoFile);
      }
      if (signatureFile) {
        form.append('signature', signatureFile);
      }
      return api.put(`/employees/${id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    // Otherwise send as JSON
    return api.put(`/employees/${id}`, data);
  },
  delete: (id) => api.delete(`/employees/${id}`),
  updateConsent: (id, consent) => api.patch(`/employees/${id}/consent`, { consent }),
  uploadPhoto: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/employees/${id}/upload/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadSignature: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/employees/${id}/upload/signature`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Document API
export const documentAPI = {
  getAll: (page = 1, pageSize = 10, groupId = null) => {
    const params = new URLSearchParams({ page, pageSize });
    if (groupId) params.append('group_id', groupId);
    return api.get(`/documents?${params}`);
  },
  getByStatus: (status, page = 1, pageSize = 10) => api.get(`/documents/status/${status}?page=${page}&pageSize=${pageSize}`),
  search: (query, { status, docType, page = 1, pageSize = 10 } = {}) =>
    api.get('/documents/search', {
      params: {
        q: query,
        status,
        doc_type: docType,
        page,
        pageSize
      }
    }),
  getById: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  updateStatus: (id, status) => api.patch(`/documents/${id}/status`, { status }),
  getByEmployee: (employeeId) => api.get(`/documents/employee/${employeeId}`),
  generateFromEmployee: (payload) => api.post('/documents/generate-from-employee', payload),
  generateAll: (payload) => api.post('/documents/generate-all', payload),
  // Get PDF and XML file URLs
  getPdfUrl: (docId) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://digilocker.srpfpunegroup1.in/api';
    return `${API_BASE_URL}/documents/${docId}/pdf`;
  },
  getXmlUrl: (docId) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://digilocker.srpfpunegroup1.in/api';
    return `${API_BASE_URL}/documents/${docId}/xml`;
  },
};

// Group ID Card Design API
export const idCardDesignAPI = {
  getConfig: (groupId) => api.get(`/group-idcard-design/${groupId}`),
  saveConfig: (groupId, payload) => api.put(`/group-idcard-design/${groupId}`, payload),
  uploadHeader: (groupId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/group-idcard-design/${groupId}/upload/header`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadSignature: (groupId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/group-idcard-design/${groupId}/upload/signature`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Helper to convert stored ./files/... path into full URL for previews
  getAssetUrl: (relativePath) => {
    if (!relativePath) return null;
    const base = (process.env.REACT_APP_API_URL || 'https://digilocker.srpfpunegroup1.in/api').replace(/\/api$/, '');
    const cleaned = relativePath.replace(/^\.\//, '');
    return `${base}/${cleaned}`;
  },
};

// DigiLocker API
export const digilockerAPI = {
  pullUriRequest: async (data, hmac) => {
    // Use fetch to send raw XML data
    const response = await fetch(`${API_BASE_URL}/pullUriRequest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/xml',
        'x-digilocker-hmac': hmac,
      },
      body: data // Send raw XML string
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const err = new Error(errorData?.error || response.statusText || 'Request failed');
      err.response = { data: errorData, status: response.status, statusText: response.statusText };
      throw err;
    }
    
    const responseData = await response.text();
    return { data: responseData };
  },
  healthCheck: () => api.get('/health'),
  testHmac: (data) => api.post('/test-hmac', data),
};

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  // admin-only
  register: (user) => api.post('/auth/register', user),
};

// User API
export const usersAPI = {
  me: () => api.get('/users/me'),
  changePassword: (currentPassword, newPassword) => api.patch('/users/me/password', { currentPassword, newPassword }),
  list: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, body) => api.patch(`/users/${id}`, body),
  resetPassword: (id, newPassword) => api.patch(`/users/${id}/reset-password`, { newPassword }),
  remove: (id) => api.delete(`/users/${id}`),
};

// Group API
export const groupAPI = {
  getAll: () => api.get('/groups'),
  getById: (id) => api.get(`/groups/${id}`),
  getStats: (id) => api.get(`/groups/${id}/stats`),
  create: (data) => api.post('/groups', data),
  update: (id, data) => api.put(`/groups/${id}`, data),
  delete: (id) => api.delete(`/groups/${id}`),
};

// Customer API
export const customerAPI = {
  getAll: () => api.get('/customers'),
  getById: (id) => api.get(`/customers/${id}`),
  getStats: (id) => api.get(`/customers/${id}/stats`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// User-Group API
export const userGroupAPI = {
  getAllUsersWithGroups: () => api.get('/user-groups/users-with-groups'),
  getUsersByGroup: (groupId) => api.get(`/user-groups/group/${groupId}/users`),
  assignUserToGroup: (data) => api.post('/user-groups/assign', data),
  updateUserGroupRole: (id, data) => api.put(`/user-groups/${id}`, data),
  removeUserFromGroup: (id) => api.delete(`/user-groups/${id}`),
};

// Department API
export const departmentAPI = {
  getAll: () => api.get('/departments'),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
};

// Designation API
export const designationAPI = {
  getAll: () => api.get('/designations'),
  getById: (id) => api.get(`/designations/${id}`),
  create: (data) => api.post('/designations', data),
  update: (id, data) => api.put(`/designations/${id}`, data),
  delete: (id) => api.delete(`/designations/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Google Sheets API
export const googleSheetsAPI = {
  getConfig: () => api.get('/google-sheets/config'),
  saveConfig: (config) => api.post('/google-sheets/config', config),
  syncEmployees: (updateExisting = false) => api.post('/google-sheets/sync', { updateExisting }),
};

// Attach access token automatically when present
let accessToken = null;
let refreshTokenMem = null;
export const authToken = {
  set: (token, refresh) => {
    accessToken = token;
    if (typeof window !== 'undefined') {
      try { localStorage.setItem('accessToken', token || ''); } catch (_) {}
    }
    if (refresh) {
      refreshTokenMem = refresh;
      if (typeof window !== 'undefined') {
        try { localStorage.setItem('refreshToken', refresh || ''); } catch (_) {}
      }
    }
  },
  clear: () => {
    accessToken = null;
    refreshTokenMem = null;
    if (typeof window !== 'undefined') {
      try { localStorage.removeItem('accessToken'); localStorage.removeItem('refreshToken'); } catch (_) {}
    }
  },
  getAccess: () => accessToken,
  getRefresh: () => refreshTokenMem,
  bootstrap: () => {
    if (typeof window !== 'undefined') {
      try {
        const a = localStorage.getItem('accessToken');
        const r = localStorage.getItem('refreshToken');
        if (a) accessToken = a;
        if (r) refreshTokenMem = r;
      } catch (_) {}
    }
  }
};

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

export default api;
